import { Component, EventEmitter, Input, Output, SimpleChanges } from "@angular/core";
import { ConditionSet, Control, DynamicForm, FormConditions } from "../../models/dynamic-form";
import { FormGroup, ReactiveFormsModule } from "@angular/forms";
import { debounceTime, distinctUntilChanged, Subscription } from "rxjs";
import { NgClass, NgStyle } from "@angular/common";
import { DynamicInputComponent } from "./dynamic-input/dynamic-input.component";
import { DynamicFormService } from "../../services/dynamic-form.service";
import { PermissionService } from "../../services/permission.service";

@Component({
  selector: "app-dynamic-form",
  standalone: true,
  imports: [
    ReactiveFormsModule,
    NgStyle,
    NgClass,
    DynamicInputComponent
  ],
  templateUrl: "./dynamic-form.component.html",
  styleUrl: "./dynamic-form.component.scss"
})
export class DynamicFormComponent {
  @Input() formConfig: DynamicForm | undefined;
  @Input() formGroup!: FormGroup;
  @Input() readOnly: boolean = false;

  private dependentFieldMap: Map<string, Set<string>> = new Map();
  private validatorDependentFieldMap: Map<string, Set<string>> = new Map();
  private controlMetadataMap: Map<string, Control> = new Map();
  private controlVisibilityMap = new Map<string, boolean>();
  private permissionVisibilityMap = new Map<string, boolean>();

  @Output() onValueChange = new EventEmitter<{ controlName: string, result: any }>();
  @Output() onValueCleared = new EventEmitter<any>();
  @Output() onSubmit = new EventEmitter<void>();


  requiredImplyingValidators = new Set([
    "required",
    "selectRequired",
    "autocompleteRequired"
  ]);

  handleValueChange(event: { controlName: string, result: any }) {
    this.onValueChange.emit(event);
  }

  onFormValueCleared(controlName: string) {
    this.onValueCleared.emit(controlName);
  }

  constructor(private dynamicFormService: DynamicFormService,
              private permissionService: PermissionService) {
  }

  ngOnInit() {
    this.listenToConditionChanges(this.formConfig);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes["formConfig"] && changes["formConfig"].currentValue) {
      this.listenToConditionChanges(this.formConfig);
    }
  }


  isControlRequired(control: Control) {
    const formControl = this.formGroup.get(control.label);
    if (!control.validators || control.validators.length === 0 || formControl?.disabled) return false;

    // Active validators are those whose conditions currently evaluate to true
    const activeValidatorConfigs = control.validators.filter(v =>
      this.dynamicFormService.evaluateConditionSet(v.conditions, this.formGroup, true)
    );

    if (activeValidatorConfigs.length === 0) return false;

    // Required if any active validator is of a required-implying type
    return activeValidatorConfigs.some(v => this.requiredImplyingValidators.has(v.type));
  }

  isControlReadOnly(control: Control) {
    return control.isReadOnly ?? false;
  }

  isControlVisible(label: string): boolean {
    //const config = this.getControlConfig(label);

    //if (!config) return false;
    //if (config.hidden) return false;
    return this.controlVisibilityMap.get(label) ?? true;
  }

  isControlDisabled(label: string): boolean {
    return this.formGroup.get(label)?.disabled ?? false;
  }

  private listenToConditionChanges(config: DynamicForm | undefined): void {
    if (!config) return;

    // 1. Build dependency map with memoization
    this.buildDependencyGraph(config);

    // 2. Set up optimized subscriptions
    this.setupConditionSubscriptions();

    // 3. Initialize visibility for all controls
    this.setUpVisibility(config);

    // 4. Initialize validators for all controls based on current conditions
    this.initializeValidators(config);
  }

  enableControl(label: string, enable: boolean) {
    const control = this.formGroup.get(label);
    if (control) {
      enable ? control.enable({ emitEvent: false }) : control.disable({ emitEvent: false });
    }
    this.controlVisibilityMap.set(label, enable);
  }

  private setUpVisibility(config: DynamicForm) {
    config.formRows.forEach(row => {
      row.formGroups.forEach(group => {
        const control = group.control;
        if (!control?.label) return;

        // Store permission-based visibility (evaluated once, doesn't change)
        const baseVisible = !(control.hidden ?? false);
        const permissionAllowed = this.dynamicFormService.evaluatePermissionConditionSet(control.permissionConditions?.visibility, baseVisible);
        this.permissionVisibilityMap.set(control.label, permissionAllowed);

        // Initialize visibility state - evaluate regular conditions with permission constraint
        const conditionVisible = this.dynamicFormService.evaluateConditionSet(control.conditions?.visibility, this.formGroup, permissionAllowed);
        this.controlVisibilityMap.set(control.label, permissionAllowed && conditionVisible);
      });
    });
  }

  private buildDependencyGraph(config: DynamicForm) {

    config.formRows.forEach(row => {
      row.formGroups.forEach(group => {
        const control = group.control;
        if (!control?.label) return;

        // 1. Track control metadata
        this.controlMetadataMap.set(control.label, control);

        // 2. Process visibility/disabled conditions
        ["visibility", "disabled"].forEach(conditionType => {
          const condition = control.conditions?.[conditionType as keyof FormConditions];
          this.addConditionDependencies(condition, control.label, this.dependentFieldMap);
        });

        // 3. Validator conditions
        (control.validators || [])
          .filter(v => !!v.conditions)
          .forEach(v => this.addConditionDependencies(v.conditions, control.label, this.validatorDependentFieldMap));
      });
    });
  }

  private addConditionDependencies(conditionSet: ConditionSet | undefined, targetField: string, dependencyMap: Map<string, Set<string>>): void {
    conditionSet?.rules.forEach(rule => {
      if (!rule.field) return;

      if (!dependencyMap.has(rule.field)) {
        dependencyMap.set(rule.field, new Set());
      }
      dependencyMap.get(rule.field)?.add(targetField);
    });
  }


  private getControlConfig(label: string): Control | undefined {
    return this.controlMetadataMap.get(label);
  }

  revaluateFormConditions() {
    this.dependentFieldMap.forEach((targetFields, dependentField) => {
      const fieldControl = this.formGroup.get(dependentField);
      if (!fieldControl) return;
      this.updateTargetFields(targetFields);
    });
  }

  private setupConditionSubscriptions(): void {
    const subscriptionCache = new Map<string, Subscription>();

    this.dependentFieldMap.forEach((targetFields, dependentField) => {
      const fieldControl = this.formGroup.get(dependentField);
      if (!fieldControl) return;

      // Clean up previous subscription if exists
      subscriptionCache.get(dependentField)?.unsubscribe();

      // Create new optimized subscription
      const sub = fieldControl.valueChanges.pipe(
        debounceTime(50),
        distinctUntilChanged()
      ).subscribe(() => {
        this.updateTargetFields(targetFields);
      });

      subscriptionCache.set(dependentField, sub);
    });

    // Subscriptions for validator-driven dependencies
    this.validatorDependentFieldMap.forEach((targetFields, dependentField) => {
      const fieldControl = this.formGroup.get(dependentField);
      if (!fieldControl) return;

      subscriptionCache.get(`validator:${dependentField}`)?.unsubscribe();

      const sub = fieldControl.valueChanges.pipe(
        debounceTime(50),
        distinctUntilChanged()
      ).subscribe(() => {
        this.updateTargetValidators(targetFields);
      });

      subscriptionCache.set(`validator:${dependentField}`, sub);
    });
  }

  private updateTargetFields(targetFields: Set<string>): void {
    targetFields.forEach(targetField => {
      const control = this.controlMetadataMap.get(targetField);
      const formControl = this.formGroup.get(targetField);
      if (!control || !formControl) return;

      // Only if visibility condition is configured
      if (control.conditions?.visibility) {
        // Get permission-based visibility (evaluated once, doesn't change)
        const permissionAllowed = this.permissionVisibilityMap.get(targetField) ?? true;

        // If permission doesn't allow visibility, always hide
        if (!permissionAllowed) {
          formControl.disable({ emitEvent: false });
          this.controlVisibilityMap.set(targetField, false);
        } else {
          // Permission allows visibility, evaluate regular conditions
          const baseVisible = !(control.hidden ?? false);
          const conditionVisible = this.dynamicFormService.evaluateConditionSet(control.conditions?.visibility, this.formGroup, baseVisible);
          conditionVisible ? formControl.enable({ emitEvent: false }) : formControl.disable({ emitEvent: false });
          this.controlVisibilityMap.set(targetField, conditionVisible);
        }
      }

      // Only if disabled condition is configured
      if (control.conditions?.disabled && !this.readOnly) {
        const disabled = this.dynamicFormService.evaluateConditionSet(control.conditions.disabled, this.formGroup, formControl.disabled);
        disabled ? formControl.disable({ emitEvent: false }) : formControl.enable({ emitEvent: false });
      }

      formControl.updateValueAndValidity({ onlySelf: true });
    });
  }

  private updateTargetValidators(targetFields: Set<string>): void {
    targetFields.forEach(targetField => {
      const control = this.controlMetadataMap.get(targetField);
      const formControl = this.formGroup.get(targetField);
      if (!control || !formControl) return;

      const validatorFns = this.dynamicFormService.getValidators(control.validators || [], this.formGroup);
      formControl.setValidators(validatorFns);
      formControl.updateValueAndValidity({ onlySelf: true, emitEvent: false });
    });
  }

  private initializeValidators(config: DynamicForm): void {
    config.formRows.forEach(row => {
      row.formGroups.forEach(group => {
        const control = group.control;
        const formControl = this.formGroup.get(control.label);
        if (!formControl) return;

        const validatorFns = this.dynamicFormService.getValidators(control.validators || [], this.formGroup);
        formControl.setValidators(validatorFns);
        formControl.updateValueAndValidity({ onlySelf: true, emitEvent: false });
      });
    });
  }
}
